import React, { useMemo } from 'react';
import katex from 'katex';

interface MathRendererProps {
  content: string;
}

/**
 * Convert plain-text math notation to LaTeX.
 * e.g., "1000·(1.5)^x" → "1000 \\cdot (1.5)^{x}"
 */
function toLatex(text: string): string {
  let latex = text.trim();
  // Middle dot (U+00B7) and dot operator (U+22C5) → \cdot
  latex = latex.replace(/[·⋅]/g, '\\cdot ');
  // Exponents: ^(expr) → ^{expr}  and  ^word → ^{word}
  latex = latex.replace(/\^(\([^)]+\))/g, (_m, inner) => `^{${inner}}`);
  latex = latex.replace(/\^([a-zA-Z0-9]+)/g, '^{$1}');
  // Arrow → \to
  latex = latex.replace(/→/g, '\\to ');
  // "+-" pattern from coefficient formatting → just minus
  latex = latex.replace(/\+\s*-/g, '- ');
  return latex;
}

/**
 * Render a LaTeX string via KaTeX, returning HTML.
 */
function renderKatex(latex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(latex, { displayMode, throwOnError: false });
  } catch {
    return `<span class="math-error">${latex}</span>`;
  }
}

/**
 * Check if a line is a standalone math equation (no English prose).
 * e.g., "2x + 3y = 5", "y = -2x + 8", "f(x) = 1000·(1.5)^x"
 */
function isMathLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length > 80) return false;
  if (!trimmed.includes('=')) return false;
  // Must have a variable (letter) involved in the equation
  if (!/[a-zA-Z]/.test(trimmed)) return false;
  // Reject lines with consecutive multi-letter words (e.g., "Constant ratio r=-2")
  // Pure math equations like "y = -2x + 8" don't have consecutive English words
  if (/[a-zA-Z]{2,}\s+[a-zA-Z]{2,}/.test(trimmed)) return false;
  // Reject lines that look like English prose
  const prosePattern = /\b(the|is|are|was|what|which|how|does|explain|solve|find|write|if|then|each|cost|per|more|than|total|number|many|much|about|mean|this|that|does|true|false|because|using|into|from|with|for|has|have|not|and|but|you|your|let|be|an?|or|do|why|its|it|they|all|can|will|would|should|also|between|both|every|give|other|she|he|his|her|their|as|on|in|at|to|of|no|so)\b/i;
  if (prosePattern.test(trimmed)) return false;
  // Should look equation-like: variables/numbers with operators
  return true;
}

/**
 * Detect and wrap inline math expressions within prose text.
 * Handles: f(x)=..., expressions with · or ^, standalone variable expressions.
 */
function wrapInlineMath(line: string): string {
  // Already has $ delimiters — skip
  if (/\$/.test(line)) return line;

  // Pattern 1: Function definitions — f(x) = ..., g(t) = ..., R(t) = ...
  // Captures "f(x) = 320·(1.06)^x" within prose like "A situation is modeled by f(x) = 320·(1.06)^x."
  // Lookahead uses \.(?!\d) to not stop at decimal points (e.g., 1.06)
  // \s[a-z]{3,} stops the match before English words (e.g., "that matches the table")
  line = line.replace(
    /([a-zA-Z]\([a-zA-Z]\)\s*=\s*)([\d\w\s·⋅^(){}+\-*/.,]+?)(?=\s[a-z]{3,}|\.(?!\d)|,\s|$|\s{2})/g,
    (_match, funcPart: string, exprPart: string) => {
      const full = (funcPart + exprPart).trim().replace(/\.$/, '');
      return renderKatex(toLatex(full), false);
    }
  );

  // Pattern 2: Standalone expressions with · or ^ not already rendered
  // e.g., "a·b^x" within text (handles both U+00B7 and U+22C5)
  line = line.replace(
    /(?<![<\w])(\d*[a-zA-Z][\d\w·⋅^()+\-*/]*[·⋅^][\d\w·⋅^()+\-*/]*)(?![>\w])/g,
    (_match, expr: string) => {
      return renderKatex(toLatex(expr), false);
    }
  );

  return line;
}

/**
 * Pre-process plain-text math content before $...$ delimiter processing.
 * Detects equation lines and inline math, converts to rendered KaTeX.
 */
function preprocessPlainMath(html: string): string {
  const parts = html.split('<br>');

  // First pass: classify each line
  const classified = parts.map(part => {
    // Skip parts that already have $ delimiters or KaTeX output
    if (/\$/.test(part) || /class="katex"/.test(part)) {
      return { type: 'other' as const, html: part };
    }

    const trimmed = part.trim();

    // Check for "Label: equation" pattern (e.g., "Solve for x: x + 6 = 19")
    const colonSplit = trimmed.match(/^(.+?):\s*(.+)$/);
    if (colonSplit) {
      const [, prefix, rest] = colonSplit;
      if (isMathLine(rest)) {
        return { type: 'other' as const, html: `${prefix}: ${renderKatex(toLatex(rest), false)}` };
      }
    }

    // Standalone equation line
    if (isMathLine(trimmed)) {
      return { type: 'math' as const, html: renderKatex(toLatex(trimmed), false) };
    }

    // Try inline math detection within prose
    return { type: 'other' as const, html: wrapInlineMath(part) };
  });

  // Second pass: group consecutive math lines into a styled block
  const output: string[] = [];
  let mathGroup: string[] = [];

  const flushMathGroup = () => {
    if (mathGroup.length > 0) {
      if (mathGroup.length >= 2) {
        // Wrap consecutive equations in a math-block div
        const inner = mathGroup.map(eq => `<div class="math-line">${eq}</div>`).join('');
        output.push(`<div class="math-block">${inner}</div>`);
      } else {
        output.push(mathGroup[0]);
      }
      mathGroup = [];
    }
  };

  for (const item of classified) {
    if (item.type === 'math') {
      mathGroup.push(item.html);
    } else {
      flushMathGroup();
      output.push(item.html);
    }
  }
  flushMathGroup();

  return output.join('<br>');
}

export const MathRenderer: React.FC<MathRendererProps> = ({ content }) => {
  const rendered = useMemo(() => {
    if (!content) return '';

    // Convert newlines to <br> for proper line separation
    let result = content.replace(/\n/g, '<br>');

    // Step 0: Protect escaped dollar signs (\$) from math processing
    result = result.replace(/\\\$/g, '@@DOLLAR@@');

    // Step 1: Process explicit LaTeX delimiters first

    // Display math: $$...$$
    result = result.replace(/\$\$([\s\S]+?)\$\$/g, (_match, tex) => {
      return renderKatex(tex.replace(/<br>/g, '\n').trim(), true);
    });

    // Inline math: $...$  (but not $$)
    result = result.replace(/\$([^$]+?)\$/g, (_match, tex) => {
      return renderKatex(tex.trim(), false);
    });

    // Handle bare LaTeX commands outside $ delimiters
    result = result.replace(/\\(frac|sqrt|sum|prod|int|lim|sin|cos|tan|log|ln)\b[^<]*/g, (match) => {
      return renderKatex(match.trim(), false);
    });

    // Step 2: Auto-detect plain-text math that wasn't wrapped in $...$
    result = preprocessPlainMath(result);

    // Step 3: Restore escaped dollar signs as literal $
    result = result.replace(/@@DOLLAR@@/g, '$');

    return result;
  }, [content]);

  return (
    <span
      className="math-content"
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
};
